// ═══════════════════════════════════════════════════════════════════════════════
// Helper — confirm settlement & lock (selected period from URL)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { HelperFloatingConfirmBar } from '../../components/settlement/HelperFloatingConfirmBar';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { resolveSettleableHelperMainBudgets } from '../../utils/employerMoneyGivenSettlement';
import { USER_ROLE } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../components/ui/ToastProvider';
import { resolveApiErrorMessage } from '../../i18n/mapApiClientError';
import { budgetApi, settlementApi } from '../../lib/api';
import {
  MODAL_BACKDROP_BOTTOM_CLASS,
  MODAL_BACKDROP_SCRIM_CLASS,
  MODAL_PANEL_SCROLL_DVH_CLASS,
} from '../../lib/modalClasses';
import { AppButton } from '../../components/ui/AppButton';
import { formatIsoDateForDailyBreakdownTable, isIsoDateYmd } from '../../lib/settlementDisplayFormat';
import { HelperMoneyReceivedPeriodSelect } from '../../components/settlement/HelperMoneyReceivedPeriodSelect';
import { MoneyReceivedDetailModal } from '../../components/settlement/MoneyReceivedDetailModal';
import { ModalCircleCloseButton } from '../../components/ui/ModalCircleCloseButton';
import { getCurrentWeekRangeByTimezone } from '../../lib/currentWeekRange';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import type { BudgetListItem, SettlementListItem, SettlementSummary } from '@fdh/shared';

const defaultHelperDateRange = (timezone: string): { start: string; end: string } => {
  const weekRange = getCurrentWeekRangeByTimezone(timezone);
  return {
    start: weekRange.startDate,
    end: weekRange.endDate,
  };
};

const sortSettlementsOldestToNewest = (rows: SettlementListItem[]): SettlementListItem[] => {
  return [...rows].sort((a, b) => {
    if (a.startDate !== b.startDate) {
      return a.startDate.localeCompare(b.startDate);
    }
    if (a.endDate !== b.endDate) {
      return a.endDate.localeCompare(b.endDate);
    }
    return a.id.localeCompare(b.id);
  });
};

const getClosestBudgetByStartDate = (budgets: BudgetListItem[], todayYmd: string): BudgetListItem | null => {
  const today = new Date(`${todayYmd}T00:00:00`);
  if (Number.isNaN(today.getTime())) {
    return null;
  }
  return budgets.reduce<BudgetListItem | null>((closest, row) => {
    const rowStart = new Date(`${row.startDate}T00:00:00`);
    if (Number.isNaN(rowStart.getTime())) {
      return closest;
    }
    if (!closest) {
      return row;
    }
    const closestStart = new Date(`${closest.startDate}T00:00:00`);
    if (Number.isNaN(closestStart.getTime())) {
      return row;
    }
    const rowDistance = Math.abs(rowStart.getTime() - today.getTime());
    const closestDistance = Math.abs(closestStart.getTime() - today.getTime());
    if (rowDistance < closestDistance) {
      return row;
    }
    if (rowDistance > closestDistance) {
      return closest;
    }
    return row.startDate > closest.startDate ? row : closest;
  }, null);
};


export const HelperSettlementConfirmPage = () => {
  const { t } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [preview, setPreview] = useState<SettlementSummary | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [dailyDetailModalOpen, setDailyDetailModalOpen] = useState(false);
  const [moneyReceivedDetailModalOpen, setMoneyReceivedDetailModalOpen] = useState(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [emptyReceiptModalOpen, setEmptyReceiptModalOpen] = useState(false);
  const [allSettlements, setAllSettlements] = useState<SettlementListItem[]>([]);
  const [mainBudgets, setMainBudgets] = useState<BudgetListItem[]>([]);

  const lockModalConfirmRef = useRef<HTMLButtonElement>(null);
  const emptyModalConfirmRef = useRef<HTMLButtonElement>(null);

  const isHelper = user?.role === USER_ROLE.HELPER;
  const timezone = resolveDisplayTimeZone(user?.settings.timezone);

  const selectedMainBudgetId = searchParams.get('mainBudgetId') ?? '';
  const settleableMainBudgets = useMemo(
    () => resolveSettleableHelperMainBudgets(mainBudgets, allSettlements),
    [allSettlements, mainBudgets]
  );
  const selectedMainBudget = useMemo(
    () => settleableMainBudgets.find((row) => row.id === selectedMainBudgetId) ?? null,
    [selectedMainBudgetId, settleableMainBudgets]
  );
  const helperSettlementStart = selectedMainBudget?.startDate ?? defaultHelperDateRange(timezone).start;
  const helperSettlementEnd = selectedMainBudget?.endDate ?? defaultHelperDateRange(timezone).end;

  const backToExpensesHref = useMemo(() => {
    const p = new URLSearchParams();
    if (selectedMainBudgetId) {
      p.set('mainBudgetId', selectedMainBudgetId);
    }
    return `/settlements?${p.toString()}`;
  }, [selectedMainBudgetId]);


  useEffect(() => {
    if (settleableMainBudgets.length === 0) {
      return;
    }
    const hasSelectedSettleableBudget = settleableMainBudgets.some(
      (row) => row.id === selectedMainBudgetId
    );
    if (hasSelectedSettleableBudget) {
      return;
    }
    const today = defaultHelperDateRange(timezone).start;
    const current = settleableMainBudgets.find((row) => row.startDate <= today && row.endDate >= today);
    const closest = getClosestBudgetByStartDate(settleableMainBudgets, today);
    const nextBudget = current ?? closest;
    if (!nextBudget) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('mainBudgetId', nextBudget.id);
    setSearchParams(next, { replace: true });
  }, [searchParams, selectedMainBudgetId, setSearchParams, settleableMainBudgets, timezone]);

  useEffect(() => {
    if (!isHelper) {
      return;
    }
    const loadMainBudgets = async () => {
      const res = await budgetApi.list('active', 1, 100, '2000-01-01', '9999-12-31');
      if (!res.success || !res.data) {
        setMainBudgets([]);
        return;
      }
      const rows = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
      setMainBudgets(rows);
    };
    void loadMainBudgets();
  }, [isHelper]);

  useEffect(() => {
    if (!isHelper) {
      return;
    }
    const loadAllSettlements = async () => {
      let page = 1;
      const pageSize = 100;
      let merged: SettlementListItem[] = [];
      while (page <= 20) {
        const res = await settlementApi.list('all', page, pageSize);
        if (!res.success || !res.data) {
          break;
        }
        const rows = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        merged = [...merged, ...rows];
        if (rows.length < pageSize) {
          break;
        }
        page += 1;
      }
      setAllSettlements(merged);
    };
    void loadAllSettlements();
  }, [isHelper]);

  const runHelperPreview = useCallback(async () => {
    if (!selectedMainBudget) {
      return;
    }
    setPreviewLoading(true);
    setFormError(null);
    setPreview(null);
    try {
      const res = await settlementApi.preview({
        mainBudgetId: selectedMainBudget.id,
      });
      if (res.success && res.data) {
        setPreview(res.data);
      } else {
        const errorMessage = res.error ?? t('settlement.error');
        if (
          errorMessage.includes('pending employer decision') ||
          errorMessage.includes('already settled and locked')
        ) {
          const overlapStatusList = errorMessage.includes('pending employer decision')
            ? ['submitted']
            : ['reviewed', 'submitted'];
          const overlapRows = sortSettlementsOldestToNewest(
            allSettlements.filter(
              (row) =>
                overlapStatusList.includes(row.status) &&
                row.startDate <= helperSettlementEnd &&
                row.endDate >= helperSettlementStart
            )
          );
          if (overlapRows.length > 0) {
            const ranges = overlapRows.map((row) => `${row.startDate} to ${row.endDate}`).join(', ');
            setFormError(
              errorMessage.includes('pending employer decision')
                ? t('settlement.confirmOverlapSubmittedRangesError', { ranges })
                : t('settlement.confirmOverlapLockedRangesError', { ranges })
            );
            return;
          }
        }
        setFormError(errorMessage);
      }
    } catch (err) {
      const errorMessage = resolveApiErrorMessage(err, t, 'settlement.error');
      if (
        errorMessage.includes('pending employer decision') ||
        errorMessage.includes('already settled and locked')
      ) {
        const overlapStatusList = errorMessage.includes('pending employer decision')
          ? ['submitted']
          : ['reviewed', 'submitted'];
        const overlapRows = sortSettlementsOldestToNewest(
          allSettlements.filter(
            (row) =>
              overlapStatusList.includes(row.status) &&
              row.startDate <= helperSettlementEnd &&
              row.endDate >= helperSettlementStart
          )
        );
        if (overlapRows.length > 0) {
          const ranges = overlapRows.map((row) => `${row.startDate} to ${row.endDate}`).join(', ');
          setFormError(
            errorMessage.includes('pending employer decision')
              ? t('settlement.confirmOverlapSubmittedRangesError', { ranges })
              : t('settlement.confirmOverlapLockedRangesError', { ranges })
          );
          return;
        }
      }
      setFormError(errorMessage);
    } finally {
      setPreviewLoading(false);
    }
  }, [allSettlements, helperSettlementEnd, helperSettlementStart, selectedMainBudget, t]);

  useEffect(() => {
    if (!selectedMainBudget) {
      setPreview(null);
      setFormError(null);
      return undefined;
    }
    setFormError(null);
    const timerId = window.setTimeout(() => {
      void runHelperPreview();
    }, 400);
    return () => window.clearTimeout(timerId);
  }, [runHelperPreview, selectedMainBudget, t]);

  useEffect(() => {
    if (lockModalOpen) {
      lockModalConfirmRef.current?.focus();
    }
  }, [lockModalOpen]);

  useEffect(() => {
    if (emptyReceiptModalOpen) {
      emptyModalConfirmRef.current?.focus();
    }
  }, [emptyReceiptModalOpen]);

  useEffect(() => {
    if (!lockModalOpen && !emptyReceiptModalOpen && !dailyDetailModalOpen && !moneyReceivedDetailModalOpen) {
      return undefined;
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      e.preventDefault();
      if (submitLoading) {
        return;
      }
      if (dailyDetailModalOpen) {
        setDailyDetailModalOpen(false);
        return;
      }
      if (moneyReceivedDetailModalOpen) {
        setMoneyReceivedDetailModalOpen(false);
        return;
      }
      if (emptyReceiptModalOpen) {
        setEmptyReceiptModalOpen(false);
        return;
      }
      setLockModalOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [dailyDetailModalOpen, emptyReceiptModalOpen, lockModalOpen, moneyReceivedDetailModalOpen, submitLoading]);

  const handleOpenLockModal = () => {
    setFormError(null);
    if (previewLoading) {
      setFormError(t('settlement.previewing'));
      return;
    }
    if (!preview) {
      setFormError(t('settlement.previewFirst'));
      return;
    }
    setLockModalOpen(true);
  };

  const handleCloseLockModal = () => {
    if (submitLoading) {
      return;
    }
    setLockModalOpen(false);
  };

  const handleCloseEmptyReceiptModal = () => {
    if (submitLoading) {
      return;
    }
    setEmptyReceiptModalOpen(false);
  };

  const submitSettlementLock = async (acknowledgeNoReceipts: boolean) => {
    if (!preview) {
      return;
    }
    setSubmitLoading(true);
    setFormError(null);
    try {
      const res = await settlementApi.create({
        mainBudgetId: selectedMainBudget?.id ?? selectedMainBudgetId,
        acknowledgeNoReceipts,
      });
      if (res.success && res.data?.settlement?.id) {
        setLockModalOpen(false);
        setEmptyReceiptModalOpen(false);
        showSuccessToast(t('toast.settlement.confirmSuccess'));
        navigate(`/settlements/${res.data.settlement.id}`, { replace: true });
      } else {
        setFormError(res.error ?? t('settlement.error'));
      }
    } catch (err) {
      setFormError(resolveApiErrorMessage(err, t, 'settlement.error'));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleConfirmLockModal = () => {
    if (!preview) {
      return;
    }
    setLockModalOpen(false);
    if (preview.receiptCount === 0) {
      setEmptyReceiptModalOpen(true);
      return;
    }
    void submitSettlementLock(false);
  };

  const handleConfirmEmptyReceiptModal = () => {
    void submitSettlementLock(true);
  };

  const formatMoney = (n: number) => (n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`);
  const formatMoneyCompact = (n: number) => `$${Number(n.toFixed(2)).toString()}`;

  const balanceLabel = (balance: number) => {
    if (balance >= 0) return t('settlement.surplus');
    return t('settlement.overspent');
  };

  const balanceClass = (balance: number) =>
    balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';

  const dailyTableRangeSameCalendarYear = useMemo(() => {
    if (!isIsoDateYmd(helperSettlementStart) || !isIsoDateYmd(helperSettlementEnd)) {
      return false;
    }
    return helperSettlementStart.slice(0, 4) === helperSettlementEnd.slice(0, 4);
  }, [helperSettlementEnd, helperSettlementStart]);

  if (!isHelper) {
    return <Navigate to="/settlements" replace />;
  }

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to={backToExpensesHref}
              aria-label={t('task.action.back')}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4L6 10l6 6" />
              </svg>
            </Link>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {t('settlement.helperConfirmPageTitle')}
            </h1>
          </div>
        </div>
      </header>

      <main
        className={`mx-auto max-w-lg px-4 py-6 ${preview ? SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW : 'pb-24'}`}
      >
        <section className="mb-4" aria-labelledby="helper-confirm-date-range-heading">
          <h2
            id="helper-confirm-date-range-heading"
            className="text-base font-semibold text-gray-900 dark:text-gray-100"
          >
            {t('settlement.helperConfirmDateRangeLabel')}
          </h2>
          <div className="mt-2">
          <HelperMoneyReceivedPeriodSelect
            id="helper-confirm-main-budget-select"
            ariaLabel={t('settlement.helperConfirmDateRangeLabel')}
            budgets={settleableMainBudgets}
            value={selectedMainBudget ? selectedMainBudgetId : ''}
            onChange={(budgetId) => {
              const params = new URLSearchParams(searchParams);
              params.set('mainBudgetId', budgetId);
              setSearchParams(params, { replace: true });
            }}
            disabled={settleableMainBudgets.length === 0}
          />
          </div>
        </section>

        {formError ? (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        ) : null}
        {previewLoading && !preview ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('settlement.previewing')}</p>
        ) : null}
        {preview ? (
          <div className="space-y-4">
            <section aria-labelledby="helper-expense-preview-heading">
              <h2
                id="helper-expense-preview-heading"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                {t('settlement.expensePreview')}
              </h2>
              <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/20">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                    <span>{t('settlement.helperStatPeriodBudget')}</span>
                    <button
                      type="button"
                      onClick={() => setMoneyReceivedDetailModalOpen(true)}
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
                      aria-label={t('settlement.moneyReceivedDetailOpen')}
                      aria-haspopup="dialog"
                      aria-expanded={moneyReceivedDetailModalOpen}
                    >
                      <span aria-hidden>i</span>
                    </button>
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                    {formatMoneyCompact(preview.settlement.budgetAmount)}
                  </span>
                </div>
                <div className="my-3 border-t border-dashed border-gray-200 dark:border-gray-600" />
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                    <span>{t('settlement.helperStatTotalSpent')}</span>
                    <button
                      type="button"
                      onClick={() => setDailyDetailModalOpen(true)}
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
                      aria-label={t('settlement.dailyDetailOpen')}
                      aria-haspopup="dialog"
                      aria-expanded={dailyDetailModalOpen}
                    >
                      <span aria-hidden>i</span>
                    </button>
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                    {formatMoneyCompact(preview.settlement.totalSpent)}
                  </span>
                </div>
                <div className="my-3 border-t border-dashed border-gray-200 dark:border-gray-600" />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {balanceLabel(preview.settlement.balance)}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap text-lg font-bold tracking-tight tabular-nums ${balanceClass(preview.settlement.balance)}`}
                  >
                    {preview.settlement.balance < 0 ? (
                      <svg className="h-[0.9em] w-[0.9em] shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM6.75 9.25a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg className="h-[0.9em] w-[0.9em] shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.25a.75.75 0 00-1.5 0v2.5h-2.5a.75.75 0 000 1.5h2.5v2.5a.75.75 0 001.5 0v-2.5h2.5a.75.75 0 000-1.5h-2.5v-2.5z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    <span>{formatMoneyCompact(Math.abs(preview.settlement.balance))}</span>
                  </span>
                </div>
              </div>
            </section>

            <div
              className="flex gap-3 rounded-xl border border-emerald-100 bg-emerald-50/90 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/30"
              role="note"
            >
              <svg
                className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <p className="text-sm leading-relaxed text-emerald-900 dark:text-emerald-100/90">
                {t('settlement.helperConfirmLockNotice')}
              </p>
            </div>

            <section aria-labelledby="helper-confirm-receipts-heading">
              <h2
                id="helper-confirm-receipts-heading"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                {t('settlement.receiptsTitle')}
              </h2>
              {preview.receipts.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('settlement.empty')}</p>
              ) : (
                <ul className="mt-2 space-y-2" role="list">
                  {preview.receipts.map((receiptRow) => (
                    <li
                      key={receiptRow.id}
                      className="rounded-lg bg-white p-3 shadow-sm dark:bg-gray-900 dark:shadow-black/20"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2 text-sm">
                        <div>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-gray-600 dark:text-gray-300">{receiptRow.receiptDate}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {formatMoney(receiptRow.totalAmount)}
                            </span>
                          </div>
                          {receiptRow.company ? (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{receiptRow.company}</p>
                          ) : null}
                        </div>
                        <Link
                          to={`/receipts/${receiptRow.id}`}
                          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                          tabIndex={0}
                          aria-label={t('settlement.viewReceipt')}
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </main>

      {preview ? (
        <HelperFloatingConfirmBar
          onConfirm={handleOpenLockModal}
          disabled={submitLoading || previewLoading}
          label={t('settlement.helperConfirmPrimary')}
          loadingLabel={t('settlement.confirming')}
          isLoading={submitLoading}
          ariaLabel={t('settlement.helperConfirmPrimary')}
        />
      ) : null}

      {dailyDetailModalOpen && preview ? (
        <div
          className={`${MODAL_BACKDROP_BOTTOM_CLASS} z-50 ${MODAL_BACKDROP_SCRIM_CLASS}`}
          role="presentation"
          onClick={() => setDailyDetailModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="settlement-daily-detail-title"
            className={`flex w-full max-w-md flex-col rounded-xl bg-white p-5 shadow-lg dark:bg-gray-900 dark:ring-1 dark:ring-gray-700 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3
                  id="settlement-daily-detail-title"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  {t('settlement.breakdownModalTitle')}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {t('settlement.period', { start: preview.settlement.startDate, end: preview.settlement.endDate })}
                </p>
              </div>
              <ModalCircleCloseButton
                onClick={() => setDailyDetailModalOpen(false)}
                ariaLabel={t('common.closeViewer')}
              />
            </div>
            <div className="mt-4 overflow-y-auto pr-1">
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                        {t('settlement.dailyDetailDate')}
                      </th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-200">
                        {t('settlement.dailyDetailReceiptCount')}
                      </th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-200">
                        {t('settlement.dailyDetailAmount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {preview.dailyBreakdown.map((row) => (
                      <tr key={row.date}>
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                          {formatIsoDateForDailyBreakdownTable(row.date, dailyTableRangeSameCalendarYear)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-200">
                          {row.receiptCount}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                          {formatMoneyCompact(row.totalAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {moneyReceivedDetailModalOpen && preview ? (
        <MoneyReceivedDetailModal
          open
          onClose={() => setMoneyReceivedDetailModalOpen(false)}
          settlementStart={preview.settlement.startDate}
          settlementEnd={preview.settlement.endDate}
          rows={preview.moneyReceivedDetails}
          t={t}
          titleKey="settlement.moneyReceivedDetailTitle"
          titleId="settlement-money-received-detail-title"
        />
      ) : null}

      {lockModalOpen ? (
        <div
          className={`${MODAL_BACKDROP_BOTTOM_CLASS} z-50 ${MODAL_BACKDROP_SCRIM_CLASS}`}
          role="presentation"
          onClick={handleCloseLockModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="settlement-lock-dialog-title"
            aria-describedby="settlement-lock-dialog-desc"
            className={`w-full max-w-md rounded-xl bg-white p-5 shadow-lg dark:bg-gray-900 dark:ring-1 dark:ring-gray-700 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="settlement-lock-dialog-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {t('settlement.confirmLockModalTitle')}
            </h3>
            <p id="settlement-lock-dialog-desc" className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {t('settlement.confirmLockPrompt', {
                start: helperSettlementStart,
                end: helperSettlementEnd,
              })}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <AppButton
                type="button"
                variant="ghost"
                onClick={handleCloseLockModal}
                disabled={submitLoading}
              >
                {t('common.cancel')}
              </AppButton>
              <AppButton
                ref={lockModalConfirmRef}
                type="button"
                variant="primary"
                onClick={handleConfirmLockModal}
                disabled={submitLoading}
                aria-label={t('common.confirm')}
              >
                {t('settlement.confirmAction')}
              </AppButton>
            </div>
          </div>
        </div>
      ) : null}

      {emptyReceiptModalOpen ? (
        <div
          className={`${MODAL_BACKDROP_BOTTOM_CLASS} z-50 ${MODAL_BACKDROP_SCRIM_CLASS}`}
          role="presentation"
          onClick={handleCloseEmptyReceiptModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="settlement-empty-dialog-title"
            aria-describedby="settlement-empty-dialog-desc"
            className={`w-full max-w-md rounded-xl bg-white p-5 shadow-lg dark:bg-gray-900 dark:ring-1 dark:ring-gray-700 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="settlement-empty-dialog-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {t('settlement.confirmEmptyModalTitle')}
            </h3>
            <p id="settlement-empty-dialog-desc" className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {t('settlement.confirmEmptyReceiptsPrompt')}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <AppButton
                type="button"
                variant="ghost"
                onClick={handleCloseEmptyReceiptModal}
                disabled={submitLoading}
              >
                {t('common.cancel')}
              </AppButton>
              <AppButton
                ref={emptyModalConfirmRef}
                type="button"
                variant="primary"
                onClick={handleConfirmEmptyReceiptModal}
                disabled={submitLoading}
                aria-label={t('settlement.confirm')}
              >
                {submitLoading ? t('settlement.confirming') : t('settlement.confirmAction')}
              </AppButton>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
};
